import { Box, Flex, IconButton, Text } from "@chakra-ui/react"
import { useQueryClient } from "@tanstack/react-query"
import { FiChevronLeft, FiLogOut } from "react-icons/fi"

import type { UserPublic } from "@/client"
import useAuth from "@/hooks/useAuth"
import { isEmbeddedSession } from "@/utils"
import {
  DrawerBackdrop,
  DrawerBody,
  DrawerCloseTrigger,
  DrawerContent,
  DrawerRoot,
} from "../ui/drawer"
import OrganizationSwitcher from "./OrganizationSwitcher"
import SidebarItems from "./SidebarItems"

interface SidebarProps {
  isCollapsed: boolean
  onCollapse: () => void
  mobileOpen: boolean
  onMobileOpenChange: (open: boolean) => void
}

const Sidebar = ({
  isCollapsed,
  onCollapse,
  mobileOpen,
  onMobileOpenChange,
}: SidebarProps) => {
  const queryClient = useQueryClient()
  const currentUser = queryClient.getQueryData<UserPublic>(["currentUser"])
  const { logout } = useAuth()
  const isEmbed = isEmbeddedSession()

  const handleLogout = async () => {
    logout()
  }

  return (
    <>
      {/* Mobile */}
      <DrawerRoot
        placement="start"
        open={mobileOpen}
        onOpenChange={(e) => onMobileOpenChange(e.open)}
      >
        <DrawerBackdrop />
        <DrawerContent maxW="280px">
          <DrawerCloseTrigger />
          <DrawerBody>
            <Flex flexDir="column" justify="space-between">
              <Box>
                <Box px={4} py={2} mb={2}>
                  <OrganizationSwitcher />
                </Box>
                <SidebarItems onClose={() => onMobileOpenChange(false)} />
                {!isEmbed && (
                  <Flex
                    as="button"
                    onClick={handleLogout}
                    alignItems="center"
                    gap={4}
                    px={4}
                    py={2}
                  >
                    <FiLogOut />
                    <Text>Log Out</Text>
                  </Flex>
                )}
              </Box>
              {currentUser?.email && (
                <Text fontSize="sm" p={2}>
                  Logged in as: {currentUser.email}
                </Text>
              )}
            </Flex>
          </DrawerBody>
          <DrawerCloseTrigger />
        </DrawerContent>
      </DrawerRoot>

      {/* Desktop */}

      <Box
        display={{ base: "none", md: isCollapsed ? "none" : "flex" }}
        position="sticky"
        bg="bg.subtle"
        top={0}
        minW="280px"
        h="100vh"
        p={4}
      >
        <Box w="100%">
          <Flex align="center" justify="space-between" mb={2} gap={2}>
            <Box flex="1">
              <OrganizationSwitcher />
            </Box>
            <IconButton
              aria-label="Collapse sidebar"
              variant="ghost"
              size="sm"
              onClick={onCollapse}
            >
              <FiChevronLeft />
            </IconButton>
          </Flex>
          <SidebarItems />
        </Box>
      </Box>
    </>
  )
}

export default Sidebar
